import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { http } from "../lib/api";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { UpgradeDialog } from "../components/UpgradeDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const Field = ({ label, ...props }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-widest text-[#5C564E]">{label}</Label>
    <Input {...props} />
  </div>
);

export default function AccountPage() {
  const { credits, refreshCredits } = useAuth();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const navigate = useNavigate();
  const company = credits?.company || {};
  const [form, setForm] = useState({
    name: company.name || "",
    phone: company.phone || "",
    address: company.address || "",
    logo: company.logo || "",
  });
  const [busy, setBusy] = useState(false);

  // keep the form in sync once account data loads (and after a save refresh)
  useEffect(() => {
    if (credits?.company) {
      setForm({
        name: credits.company.name || "",
        phone: credits.company.phone || "",
        address: credits.company.address || "",
        logo: credits.company.logo || "",
      });
    }
  }, [credits]);

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 320;
        let { width, height } = img;
        if (width >= height && width > max) {
          height = Math.round((height * max) / width);
          width = max;
        } else if (height > max) {
          width = Math.round((width * max) / height);
          height = max;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        setForm((f) => ({ ...f, logo: canvas.toDataURL("image/png") }));
      };
      img.onerror = () => toast.error("Could not read that image.");
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setBusy(true);
    try {
      await http.put("/account/company", form);
      await refreshCredits();
      toast.success("Saved. This prints on every quote PDF.");
    } catch (e) {
      const code = e.response?.status ? ` (${e.response.status})` : "";
      toast.error(`Could not save${code}. Try a smaller logo or retry.`);
    } finally {
      setBusy(false);
    }
  };

  const planLabel =
    credits?.lifetime
      ? "Lifetime Pro"
      : credits?.plan === "monthly"
      ? credits?.subscription_cancel
        ? "Monthly — set to not renew"
        : "Monthly ($19)"
      : "Free";

  return (
    <div className="min-h-screen">
      <Header onUpgrade={() => setUpgradeOpen(true)} />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="font-serif text-4xl font-bold text-[#1C1915] mb-8">Account</h1>

        <section className="bg-white/80 border border-[#E2DCD2] rounded-xl shadow-sm p-6 sm:p-8">
          <h3 className="font-semibold text-[#1C1915] mb-1">Company details</h3>
          <p className="text-sm text-[#5C564E] mb-5">Stored once, printed on every later PDF.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Company name" data-testid="account-company-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Field label="Phone" data-testid="account-company-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <div className="sm:col-span-2">
              <Field label="Address (optional)" data-testid="account-company-address" placeholder="123 Market St, Austin, TX 78701" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-4">
            <div className="w-16 h-16 border border-[#A6844A] rounded-md flex items-center justify-center overflow-hidden bg-[#F4F0E8]">
              {form.logo ? (
                <img src={form.logo} alt="logo" className="w-full h-full object-contain p-1" />
              ) : (
                <svg viewBox="0 0 40 40" className="w-8 h-8">
                  <polygon points="20,8 32,30 8,30" fill="#1B3A4B" />
                </svg>
              )}
            </div>
            <label className="cursor-pointer inline-flex items-center gap-2 text-sm text-[#1B3A4B] border border-[#D5CFC2] rounded-full px-4 py-2 hover:bg-[#F4F0E8]">
              <Upload className="w-4 h-4" /> Upload logo
              <input data-testid="account-logo-upload" type="file" accept="image/*" className="hidden" onChange={onLogo} />
            </label>
            {form.logo && (
              <button className="text-sm text-[#9E2A2B]" onClick={() => setForm({ ...form, logo: "" })}>
                Remove
              </button>
            )}
          </div>
          <Button data-testid="account-save" onClick={save} disabled={busy} className="mt-6 rounded-full bg-[#1B3A4B] hover:bg-[#132A37] text-white h-11 px-6">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </section>

        <section className="mt-6 bg-white/80 border border-[#E2DCD2] rounded-xl shadow-sm p-6 sm:p-8">
          <h3 className="font-semibold text-[#1C1915] mb-4">Plan & searches</h3>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-[#5C564E]">Current plan</div>
              <div className="font-serif text-xl text-[#1B3A4B]" data-testid="account-plan">{planLabel}</div>
            </div>
            <Button data-testid="account-manage-plan" onClick={() => setUpgradeOpen(true)} variant="outline" className="rounded-full border-[#A6844A] text-[#1B3A4B]">
              Manage
            </Button>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div className="rounded-lg bg-[#F4F0E8] border border-[#D5CFC2] p-4">
              <div className="text-[#5C564E]">This month</div>
              <div className="font-serif text-2xl text-[#1C1915]">{credits?.lifetime ? "∞" : credits?.plan === "monthly" ? credits.monthly_searches_remaining : 0}</div>
            </div>
            <div className="rounded-lg bg-[#F4F0E8] border border-[#D5CFC2] p-4">
              <div className="text-[#5C564E]">Pack searches</div>
              <div className="font-serif text-2xl text-[#1C1915]">{credits?.lifetime ? "∞" : credits?.pack_searches_remaining || 0}</div>
            </div>
          </div>
        </section>
      </main>
      <UpgradeDialog open={upgradeOpen} onOpenChange={(v) => { setUpgradeOpen(v); if (!v) refreshCredits(); }} />
      <Footer />
    </div>
  );
}
